import { useTheme } from '@mui/material/styles'
import { throttle } from 'lodash'
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Layer, type MapLayerMouseEvent, Source, useMap } from 'react-map-gl/maplibre'
import { getMapSourceGroups } from './KNMapLibre.service'
import { type KNMapLibreSourceSelectorProps, MapSelectionMode, type MapSourceGroup, type MapSourceSelection } from './types'

const KNMapLibreSourceSelector = ({
  source,
  mode,
  selections = [],
  onChange,
}: KNMapLibreSourceSelectorProps): ReactNode => {
  const theme = useTheme()
  const { current: map } = useMap()
  const [groups, setGroups] = useState<MapSourceGroup[]>([])
  const [hoveredGroupId, setHoveredGroupId] = useState<number | null>(null)

  const validSelections = useMemo(() => {
    return selections.filter(
      (selection) => selection.mode === MapSelectionMode.SOURCE && (selection as MapSourceSelection).source === source,
    ) as MapSourceSelection[]
  }, [source, selections])

  const updateSelectionState = useCallback(() => {
    if (!map) {
      return
    }
    if (!map.isStyleLoaded()) {
      return
    }
    const restrictedGroupIds = groups.filter((group) => group.restricted).map((group) => group.id)
    map.setGlobalStateProperty('restricted_group_ids', restrictedGroupIds)
    map.removeFeatureState({ source: `selector_source_${source}`, sourceLayer: source })
    validSelections.forEach((selection) => {
      const group = groups.find((group) => group.code === selection.code)
      if (!group) {
        return
      }
      map.queryRenderedFeatures({
        layers: [`selector_source_${source}_fill`],
        filter: ['==', ['get', 'group_id'], group.id],
        validate: false, // disables filter validation for better performance
      }).forEach((feature) => {
        map.setFeatureState(
          { source: `selector_source_${source}`, sourceLayer: source, id: feature.id },
          { selected: true, color: selection.color },
        )
      })
    })
  }, [map, source, groups, validSelections])

  const updateHoverState = useCallback((groupId: number, hovered: boolean) => {
    if (!map) {
      return
    }
    map.queryRenderedFeatures({
      layers: [`selector_source_${source}_fill`],
      filter: ['==', ['get', 'group_id'], groupId],
      validate: false, // disables filter validation for better performance
    }).forEach((feature) => {
      map.setFeatureState(
        { source: `selector_source_${source}`, sourceLayer: source, id: feature.id },
        { hover: hovered },
      )
    })
  }, [map, source])

  useEffect(() => {
    (async () => setGroups(await getMapSourceGroups(source)))()
  }, [source])

  useEffect(() => {
    updateSelectionState()
  }, [map, groups, validSelections])

  useEffect(() => {
    if (!map) {
      return
    }
    map.on('load', updateSelectionState)
    map.on('click', `selector_source_${source}_fill`, handleClick)
    map.on('mousemove', `selector_source_${source}_fill`, handleMouseMoveThrottled)
    map.on('mouseleave', `selector_source_${source}_fill`, handleMouseLeave)

    return () => {
      map.off('load', updateSelectionState)
      map.off('click', `selector_source_${source}_fill`, handleClick)
      map.off('mousemove', `selector_source_${source}_fill`, handleMouseMoveThrottled)
      map.off('mouseleave', `selector_source_${source}_fill`, handleMouseLeave)
    }
  }, [map, source, mode, selections, groups, hoveredGroupId])

  const handleClick = useCallback(async (event: MapLayerMouseEvent) => {
    if (mode !== MapSelectionMode.SOURCE) {
      return
    }
    const group = groups.find((group) => group.id === event.features?.[0]?.properties['group_id'] as number)
    if (!group) {
      return
    }
    onChange({
      mode: MapSelectionMode.SOURCE,
      source: group.layerId,
      code: group.code,
    } as MapSourceSelection)
    if (hoveredGroupId) {
      updateHoverState(hoveredGroupId, false)
    }
    setHoveredGroupId(null)
    event.target.getCanvas().style.cursor = ''
  }, [groups, mode, onChange, hoveredGroupId, updateHoverState])

  const handleMouseMove = useCallback(async (event: MapLayerMouseEvent) => {
    if (mode !== MapSelectionMode.SOURCE) {
      return
    }
    const featureGroupId = event.features?.[0]?.properties['group_id'] as number
    // early bailout if nothing is hovered, or if still hovering the same thing
    if (!featureGroupId || featureGroupId === hoveredGroupId) {
      return
    }
    // clear previous highlights
    if (hoveredGroupId) {
      updateHoverState(hoveredGroupId, false)
    }
    // set new highlights
    setHoveredGroupId(featureGroupId)
    updateHoverState(featureGroupId, true)
    event.target.getCanvas().style.cursor = 'pointer'
  }, [mode, hoveredGroupId, updateHoverState])
  const handleMouseMoveThrottled = useMemo(
    () => throttle(handleMouseMove, 33, { leading: true, trailing: false }),
    [handleMouseMove],
  )

  const handleMouseLeave = useCallback(async (event: MapLayerMouseEvent) => {
    if (hoveredGroupId) {
      updateHoverState(hoveredGroupId, false)
    }
    setHoveredGroupId(null)
    event.target.getCanvas().style.cursor = ''
  }, [hoveredGroupId, updateHoverState])

  return (
    <Source
      id={`selector_source_${source}`}
      type="vector"
      tiles={[
        `${import.meta.env.VITE_RGS_TILES_URL}/${source}/{z}/{x}/{y}`,
      ]}
      promoteId="location_id"
    >
      <Layer
        id={`selector_source_${source}_restricted_fill`}
        type="fill"
        source="vector"
        source-layer={source}
        filter={[
          'in', ['get', 'group_id'], ['global-state', 'restricted_group_ids'],
        ]}
        beforeId="label-other"
        paint={{
          'fill-color': theme.palette.error.main,
          'fill-opacity': 0.1,
        }}
      />
      <Layer
        id={`selector_source_${source}_fill`}
        type="fill"
        source="vector"
        source-layer={source}
        filter={[
          '!', ['in', ['get', 'group_id'], ['global-state', 'restricted_group_ids']],
        ]}
        beforeId="label-other"
        paint={{
          'fill-color': [
            'coalesce',
            ['feature-state', 'color'],
            theme.palette.primary.main,
          ],
          'fill-opacity': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            0.5,
            ['boolean', ['feature-state', 'hover'], false],
            0.1,
            0,
          ],
        }}
      />
    </Source>
  )
}

export default KNMapLibreSourceSelector
